import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Search, Edit, Trash2, Eye, Building2,
  Filter, Download, ChevronRight, Globe, Users, Zap, ShieldAlert
} from "lucide-react";
import axios from "axios";
import { getApiUrl } from "../../config/api";
import "../../styles/UtopiaPages.css";

interface School {
  id: number;
  name: string;
  subdomain: string;
  billing_email: string;
  status: string;
  subscription_tier: string;
  user_count: number;
  admin_count: number;
  subscription_status: string;
  subscription_end: string;
  created_at: string;
}

const SchoolsManagement: React.FC = () => {
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || "";

  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl("superadmin/schools"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchools(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load schools");
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to suspend "${name}"? This will restrict access for all users in this school.`)) {
      return;
    }

    try {
      await axios.delete(getApiUrl(`superadmin/schools/${id}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSchools();
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to suspend school");
    }
  };

  const handlePermanentDelete = async (id: number, name: string) => {
    const confirmation = window.prompt(`CRITICAL ACTION: You are about to PERMANENTLY DELETE "${name}" and ALL its associated data (users, classes, tests, payroll, etc.). This action CANNOT be undone.\n\nPlease type the school name "${name}" to confirm:`);

    if (confirmation !== name) {
      if (confirmation !== null) alert("Confirmation failed. School name did not match.");
      return;
    }

    try {
      setLoading(true);
      await axios.delete(getApiUrl(`superadmin/schools/${id}/permanently`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSchools();
      alert(`School "${name}" has been permanently deleted.`);
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to permanently delete school");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const configs: Record<string, { bg: string, text: string, dot: string }> = {
      active: { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
      trial: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
      suspended: { bg: "bg-rose-50", text: "text-rose-700", dot: "bg-rose-500" },
      expired: { bg: "bg-slate-50", text: "text-slate-700", dot: "bg-slate-500" }
    };

    const config = configs[status] || configs.expired;

    return (
      <div className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 w-fit border ${config.bg} ${config.text} border-transparent`}>
        <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></span>
        {status}
      </div>
    );
  };

  const filteredSchools = schools.filter(school => {
    const matchesSearch =
      school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      school.subdomain.toLowerCase().includes(searchTerm.toLowerCase()) ||
      school.billing_email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === "all" || school.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-vh-100 bg-white">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500/20 border-t-teal-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400 font-medium text-xs tracking-wider">Loading Platform Registry...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <main className="dashboard-main p-8 bg-slate-50/20">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-100 px-3 py-1 rounded-full">System Directory</span>
            </div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Schools <span className="text-teal-600 italic font-medium">Registry</span></h1>
            <p className="text-slate-500 mt-1 font-medium text-sm">Managing {schools.length} Active Platform Instances</p>
          </div>

          <div className="flex gap-3">
            <button className="flex items-center gap-2 bg-white border border-slate-200 px-5 py-2.5 rounded-xl font-bold text-sm text-slate-600 hover:bg-slate-50 transition-all shadow-sm">
              <Download size={16} /> Export Logs
            </button>
            <button
              onClick={() => navigate("/superadmin/schools/new")}
              className="flex items-center gap-2 bg-slate-900 text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-slate-800 transition-all shadow-lg shadow-slate-200"
            >
              <Plus size={18} /> New Institute
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-100 p-4 rounded-2xl text-rose-600 font-bold mb-8 text-sm">
            {error}
          </div>
        )}

        <div className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/40 border border-slate-100 overflow-hidden">
          {/* Enhanced Search & Filter Bar */}
          <div className="p-6 border-b border-slate-50 bg-slate-50/50 flex flex-col md:flex-row gap-4">
            <div className="relative flex-1 group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-teal-600 transition-colors" />
              <input
                type="text"
                placeholder="Search by name, subdomain, or admin email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm font-medium focus:ring-4 focus:ring-teal-500/5 focus:border-teal-500/30 outline-none transition-all shadow-sm"
              />
            </div>
            <div className="flex gap-2">
              <div className="relative shrink-0">
                <Filter className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="pl-11 pr-8 py-3 bg-white border border-slate-200 rounded-2xl text-xs font-bold uppercase tracking-wider focus:ring-4 focus:ring-teal-500/5 outline-none appearance-none transition-all cursor-pointer shadow-sm"
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active Only</option>
                  <option value="trial">Trialing</option>
                  <option value="suspended">Suspended</option>
                  <option value="expired">Deactivated</option>
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto text-sm">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50/30 border-b border-slate-50">
                  <th className="px-8 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Instance Name</th>
                  <th className="px-8 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Environment</th>
                  <th className="px-8 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Population</th>
                  <th className="px-8 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="px-8 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Tier & Billing</th>
                  <th className="px-8 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredSchools.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-8 py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center">
                          <Search size={24} className="text-slate-200" />
                        </div>
                        <p className="text-sm font-bold text-slate-300 uppercase tracking-widest">No matching instances found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredSchools.map((school) => (
                    <tr key={school.id} className="group hover:bg-slate-50/30 transition-all cursor-pointer" onClick={() => navigate(`/superadmin/schools/${school.id}`)}>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center font-bold group-hover:bg-teal-600 group-hover:text-white transition-all shadow-sm">
                            {school.name[0].toUpperCase()}
                          </div>
                          <div>
                            <div className="text-sm font-bold text-slate-800 tracking-tight">{school.name}</div>
                            <div className="text-[11px] font-medium text-slate-400 mt-1">{school.billing_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-1.5 text-slate-500">
                          <Globe size={14} className="text-slate-300" />
                          <span className="text-xs font-medium lowercase tracking-tight">{school.subdomain}.msportal.com</span>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                          <div className="flex -space-x-2">
                            {[...Array(Math.min(3, school.admin_count))].map((_, i) => (
                              <div key={i} className="w-6 h-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center">
                                <Users size={12} className="text-slate-400" />
                              </div>
                            ))}
                          </div>
                          <div>
                            <span className="text-sm font-bold text-slate-700">{school.user_count || 0}</span>
                            <span className="text-[10px] ml-1 font-bold text-slate-400 uppercase">Users</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        {getStatusBadge(school.status)}
                      </td>
                      <td className="px-8 py-6">
                        <div className="inline-flex flex-col">
                          <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wide bg-slate-100 px-3 py-1 rounded-lg w-fit">
                            {school.subscription_tier || "BASE"}
                          </span>
                          {school.subscription_end && (
                            <span className="text-[10px] font-medium text-slate-400 mt-2 flex items-center gap-1.5 tracking-tight">
                              <Zap size={10} className="text-amber-500" />
                              {new Date(school.subscription_end).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-8 py-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1.5">
                          <button
                            onClick={() => navigate(`/superadmin/schools/${school.id}`)}
                            className="p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:bg-teal-50 hover:text-teal-600 transition-all"
                            title="View Instance"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => navigate(`/superadmin/schools/${school.id}/edit`)}
                            className="p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 transition-all"
                            title="Settings"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleSuspend(school.id, school.name)}
                            className="p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:bg-amber-50 hover:text-amber-600 transition-all"
                            title="Suspend"
                          >
                            <Zap size={16} />
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(school.id, school.name)}
                            className="p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:bg-rose-100 hover:text-rose-700 transition-all shadow-sm"
                            title="PERMANENT DELETE"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SchoolsManagement;
